/**
 * A space's change history as one feed of one-liners, newest first.
 *
 * Two sources meet here. The server's log (`/api/amoreg/space-activity`)
 * records what happens to the space itself: it was created, renamed or given
 * context, its client was linked, guests were let in or removed, what the
 * client sees changed, a document was created, uploaded or edited, or a
 * version of it saved. The client adds what it already holds: chats started
 * in the space and files its chats changed. An entry the server does not know how to describe is
 * dropped rather than shown raw.
 *
 * @module components/spaces/spaceActivity
 */
import type { SpaceActivityEvent } from "../../state/spaces";

export type SpaceActivityTone = "created" | "chat" | "file" | "document" | "access" | "settings";

export interface SpaceActivityItem {
  readonly id: string;
  readonly tone: SpaceActivityTone;
  /** Who did it (an email), or null when the line names no one. */
  readonly actor: string | null;
  /** The rest of the line, after the actor. */
  readonly text: string;
  readonly at: string;
}

const quoted = (value: string) => `“${value}”`;

const documentName = (subject: string | null, detail: Record<string, unknown>): string => {
  if (typeof detail.title === "string" && detail.title.trim()) return quoted(detail.title.trim());
  if (subject === "board" || detail.docId === "board") return "the Board";
  return "a document";
};

const roleWord = (role: unknown): string => (role === "editor" ? "can edit" : "can view");

/**
 * One server event as a line, or null when it is not one the page describes.
 * `orgName` names an organization id (the client), when the page knows it.
 */
export function describeSpaceEvent(
  event: SpaceActivityEvent,
  orgName: (organizationId: string) => string | null = () => null,
): SpaceActivityItem | null {
  const detail =
    event.detail !== null && typeof event.detail === "object" ? (event.detail as Record<string, unknown>) : {};
  const line = (tone: SpaceActivityTone, text: string): SpaceActivityItem => ({
    id: `server:${event.id}`,
    tone,
    actor: event.actor,
    text,
    at: event.createdAt,
  });
  switch (event.kind) {
    case "space.created":
      return line("created", "created the space");
    case "space.renamed":
      return line("settings", typeof detail.to === "string" ? `renamed the space to ${quoted(detail.to)}` : "renamed the space");
    case "space.context":
      return line("settings", "updated the space's context");
    case "client.linked": {
      const name = event.subject === null ? null : orgName(event.subject);
      return line("access", name ? `linked ${name} as the client` : "linked the client organization");
    }
    case "client.unlinked":
      return line("access", "unlinked the client organization");
    case "client.access":
      return line("access", "changed what the client sees");
    case "guest.invited":
      return line("access", `let in ${event.subject ?? "a guest"} (${roleWord(detail.role)})`);
    case "guest.role":
      return line("access", `changed ${event.subject ?? "a guest"} to ${roleWord(detail.to)}`);
    case "guest.removed":
      return line("access", `removed ${event.subject ?? "a guest"}`);
    case "document.created":
      // An upload names the file it came from.
      return line(
        "document",
        detail.file !== null && typeof detail.file === "object"
          ? `uploaded ${documentName(event.subject, detail)}`
          : `created ${documentName(event.subject, detail)}`,
      );
    case "document.edited":
      // One line per stretch of someone's editing; the server coalesces them.
      return line("document", `edited ${documentName(event.subject, detail)}`);
    case "document.version": {
      const name = documentName(event.subject, detail);
      const number = typeof detail.version === "number" ? ` ${detail.version}` : "";
      if (typeof detail.label === "string" && detail.label.trim()) {
        return line("document", `saved version${number} ${quoted(detail.label.trim())} of ${name}`);
      }
      // A checkpoint taken while people edit names no one.
      const text = `version${number} of ${name} saved automatically`;
      return { ...line("document", text.charAt(0).toUpperCase() + text.slice(1)), actor: null };
    }
    default:
      return null;
  }
}

/**
 * The feed: the server's events, the chats started in the space and the
 * files its chats changed, newest first, at most `limit`. Without a server
 * log the space's creation still anchors the feed.
 */
export function spaceActivityFeed(input: {
  readonly space: { readonly createdAt: string };
  readonly serverEvents: ReadonlyArray<SpaceActivityEvent> | null;
  readonly chats: ReadonlyArray<{ readonly id: string; readonly title: string; readonly createdAt: string }>;
  readonly chatFiles: ReadonlyArray<{
    readonly threadId: string;
    readonly threadTitle: string;
    readonly path: string;
    readonly name: string;
    readonly modifiedAt: string;
  }>;
  readonly limit: number;
  readonly orgName?: (organizationId: string) => string | null;
}): SpaceActivityItem[] {
  const server = (input.serverEvents ?? [])
    .map((event) => describeSpaceEvent(event, input.orgName))
    .filter((item): item is SpaceActivityItem => item !== null);
  const created: SpaceActivityItem[] =
    input.serverEvents !== null && input.serverEvents.some((event) => event.kind === "space.created")
      ? []
      : [{ id: "space:created", tone: "created", actor: null, text: "Space created", at: input.space.createdAt }];
  const chats: SpaceActivityItem[] = input.chats.map((chat) => ({
    id: `chat:${chat.id}`,
    tone: "chat",
    actor: null,
    text: `Chat started: ${quoted(chat.title)}`,
    at: chat.createdAt,
  }));
  const files: SpaceActivityItem[] = input.chatFiles.map((file) => ({
    id: `file:${file.threadId}:${file.path}`,
    tone: "file",
    actor: null,
    text: `${quoted(file.name)} changed in ${quoted(file.threadTitle)}`,
    at: file.modifiedAt,
  }));
  return [...server, ...created, ...chats, ...files]
    .filter((item) => !Number.isNaN(Date.parse(item.at)))
    .toSorted((left, right) => Date.parse(right.at) - Date.parse(left.at) || left.id.localeCompare(right.id))
    .slice(0, Math.max(0, input.limit));
}
